<script setup lang="ts">
import { HomeIcon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from '@heroicons/vue/24/outline'
import { useSwitchLeave } from '~/query/user-switch/user-switch.mutation'

const props = defineProps<{
    sidebarOpen: boolean,
    sidebarOpenForDesktop: boolean
}>()

const emit = defineEmits<{
    (e: 'update:sidebarOpen', value: boolean): void,
    (e: 'update:sidebarOpenForDesktop', value: boolean): void
}>()

const sidebarOpen = computed({
  get () {
    return props.sidebarOpen
  },
  set (value) {
    emit('update:sidebarOpen', value)
  }
})

const sidebarOpenForDesktop = computed({
  get () {
    return props.sidebarOpenForDesktop
  },
  set (value) {
    emit('update:sidebarOpenForDesktop', value)
  }
})

const { t } = useI18n()

const route = useRoute()

const meta = computed(() => route.meta)

const crumbs = computed(() => typeof meta.value.crumbs === 'function' ? meta.value.crumbs(route) : meta.value.crumbs)

const session = useAuth().session

const { mutate, isLoading } = useSwitchLeave()

const disconnectHandler = () => {
  if (!useAuth().isImpersonateUser.value) {
    if (confirm(t('component.header.confirm_disconnect'))) {
      useLogout()
    }
  } else {
    mutate()
  }
}

const sideBarMobileHandler = () => {
  if (!sidebarOpenForDesktop.value) {
    sidebarOpenForDesktop.value = true
  }
  sidebarOpen.value = !sidebarOpen.value
}

</script>

<template>
  <div class="header bg-white text-zinc-900 max-h-16 sticky top-0 z-50">
    <div class="flex items-center gap-2 px-2 py-5">
      <span class="isolate inline-flex rounded-md shadow-sm">
        <UTooltip class="inline-flex xl:hidden" :text="( sidebarOpen ? 'Masquer' : 'Afficher' ) + ' la barre latérale'">
          <button class="relative inline-flex items-center rounded-l-md bg-white px-1.5 py-1 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10" @click="sideBarMobileHandler">
            <ChevronDoubleLeftIcon v-if="sidebarOpen" class="w-4 h-4" />
            <ChevronDoubleRightIcon v-else class="w-4 h-4" />
          </button>
        </UTooltip>
        <UTooltip class="hidden xl:inline-flex" :text="( sidebarOpenForDesktop ? 'Masquer' : 'Afficher' ) + ' la barre latérale'">
          <button class="relative inline-flex items-center rounded-l-md bg-white px-1.5 py-1 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10" @click="sidebarOpenForDesktop = !sidebarOpenForDesktop">
            <ChevronDoubleLeftIcon v-if="sidebarOpenForDesktop" class="w-4 h-4" />
            <ChevronDoubleRightIcon v-else class="w-4 h-4" />
          </button>
        </UTooltip>
        <nav class="relative -ml-px inline-flex items-center rounded-r-md bg-white px-2 py-1 text-gray-400 ring-1 ring-inset ring-gray-300">
          <ol role="list" class="flex items-center">
            <li>
              <div>
                <NuxtLink to="/" class="text-gray-400 hover:text-gray-500">
                  <HomeIcon class="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                  <span class="sr-only">Home</span>
                </NuxtLink>
              </div>
            </li>
            <li v-for="(crumb, index) in crumbs" :key="index">
              <div v-if="crumb.label" class="flex items-center">
                <svg class="h-5 w-5 flex-shrink-0 text-gray-300" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
                </svg>
                <NuxtLink :to="crumb.path" class="text-xs font-medium text-gray-500 hover:text-gray-700">{{ crumb.label }}</NuxtLink>
              </div>
            </li>
          </ol>
        </nav>
      </span>
      <div class="flex ml-auto mr-2 mt-auto mb-auto items-center gap-1">
        <span class="text-xs">{{ new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) }}</span>
        <div class="flex flex-col text-sm">
          <span class="inline-flex items-center gap-x-1.5 rounded-full px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200">
            <svg class="h-1.5 w-1.5 fill-green-500" viewBox="0 0 6 6" aria-hidden="true">
              <circle cx="3" cy="3" r="3" />
            </svg>
            {{ session?.username ?? session?.email }}
          </span>
        </div>
        <button class="text-xs inline-flex items-center" :disabled="isLoading" @click="disconnectHandler">
          <span>Déconnexion</span>
        </button>
      </div>
    </div>
  </div>
</template>
